import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminAPI } from '../services/api';
import AdminLayout from '../components/AdminLayout';
import { 
  Users, 
  Briefcase, 
  FileText, 
  DollarSign,
  ArrowRight,
  UserPlus,
  Award,
  MessageSquare,
  ExternalLink,
  UserCog,
  Mail,
  TrendingUp
} from 'lucide-react';
import { 
  StatCard, 
  Card, 
  CardHeader, 
  CardBody,
  Badge, 
  Avatar,
  Table,
  Button,
  PageLoader
} from '../components/ui';

interface OverviewStats {
  totalUsers: number;
  totalJobs: number;
  totalApplications: number;
  activeJobs: number;
  totalPayments: number;
  totalArticles: number;
  pendingReports: number;
  totalOpportunities: number;
  activeSubscriptions: number;
  totalRevenue: number;
}

interface AnalyticsData {
  period: string;
  newUsers: number;
  newJobs: number;
  newApplications: number;
  revenue: number;
}

interface DashboardData {
  overview: OverviewStats;
  recentUsers: Array<{
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    createdAt: string;
  }>;
  recentApplications: Array<{
    _id: string;
    user: { firstName: string; lastName: string };
    job: { title: string };
    status: string;
    createdAt: string;
  }>;
  monthlyRevenue: Array<{
    _id: string;
    revenue: number;
    count: number;
  }>;
  applicationStats: Array<{
    _id: string;
    count: number;
  }>;
}

const AdminDashboard: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsRes, analyticsRes] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getAnalytics({ period: 30 })
      ]);
      setDashboardData(statsRes.data.data);
      setAnalyticsData(analyticsRes.data.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const formatCurrency = (num: number) => {
    if (num >= 1000000) return '$' + (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return '$' + (num / 1000).toFixed(1) + 'K';
    return '$' + num.toString();
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'admin': return 'danger';
      case 'business': return 'primary';
      case 'job_seeker': return 'success';
      default: return 'default';
    }
  };

  const getApplicationStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'submitted': return 'info';
      case 'reviewing': return 'warning';
      case 'shortlisted': return 'primary';
      case 'interviewed': return 'primary';
      case 'offered': return 'success';
      case 'hired': return 'success';
      case 'rejected': return 'danger';
      default: return 'default';
    }
  };

  if (loading || !dashboardData || !analyticsData) {
    return (
      <AdminLayout>
        <PageLoader message="Loading dashboard..." />
      </AdminLayout>
    );
  }

  const { overview } = dashboardData;
  const stats = [
    {
      title: 'Total Users',
      value: formatNumber(overview.totalUsers || 0),
      icon: <Users className="w-6 h-6" />,
      trend: analyticsData.newUsers > 0 ? { value: analyticsData.newUsers, isPositive: true, label: 'this month' } : undefined,
      color: 'primary' as const,
      subtitle: `${analyticsData.newUsers} new this month`
    },
    {
      title: 'Active Jobs',
      value: formatNumber(overview.activeJobs || 0),
      icon: <Briefcase className="w-6 h-6" />,
      trend: analyticsData.newJobs > 0 ? { value: analyticsData.newJobs, isPositive: true, label: 'this month' } : undefined,
      color: 'info' as const,
      subtitle: `${analyticsData.newJobs} new this month`
    },
    {
      title: 'Applications',
      value: formatNumber(overview.totalApplications || 0),
      icon: <FileText className="w-6 h-6" />,
      trend: analyticsData.newApplications > 0 ? { value: analyticsData.newApplications, isPositive: true, label: 'this month' } : undefined,
      color: 'success' as const,
      subtitle: `${analyticsData.newApplications} this month`
    },
    {
      title: 'Revenue',
      value: formatCurrency(overview.totalRevenue || 0),
      icon: <DollarSign className="w-6 h-6" />,
      trend: analyticsData.revenue > 0 ? { value: Math.round((analyticsData.revenue / (overview.totalRevenue || 1)) * 100), isPositive: true, label: 'this month' } : undefined,
      color: 'warning' as const,
      subtitle: `${formatCurrency(analyticsData.revenue)} this month`
    }
  ];

  const quickActions = [
    { label: 'Manage Users', icon: <UserCog className="w-5 h-5" />, path: '/admin/users', color: 'primary' },
    { label: 'View Jobs', icon: <Briefcase className="w-5 h-5" />, path: '/admin/jobs', color: 'info' },
    { label: 'Articles', icon: <FileText className="w-5 h-5" />, path: '/admin/articles', color: 'success' },
    { label: 'Opportunities', icon: <Award className="w-5 h-5" />, path: '/admin/opportunities', color: 'warning' },
  ];

  const userColumns = [
    {
      key: 'user',
      header: 'User',
      render: (item: typeof dashboardData.recentUsers[0]) => (
        <div className="flex items-center gap-3">
          <Avatar name={`${item.firstName} ${item.lastName}`} size="sm" />
          <div>
            <p className="font-medium text-secondary-900">{item.firstName} {item.lastName}</p>
            <p className="text-xs text-secondary-500">{item.email}</p>
          </div>
        </div>
      )
    },
    {
      key: 'role',
      header: 'Role',
      width: '120px',
      render: (item: typeof dashboardData.recentUsers[0]) => (
        <Badge variant={getRoleBadgeVariant(item.role)}>{item.role.replace('_', ' ')}</Badge>
      )
    },
    {
      key: 'joined',
      header: 'Joined',
      width: '140px',
      render: (item: typeof dashboardData.recentUsers[0]) => (
        <span className="text-secondary-600">{formatDate(item.createdAt)}</span>
      )
    },
    {
      key: 'actions',
      header: '',
      width: '60px',
      render: () => (
        <Button variant="ghost" size="sm">
          <ExternalLink className="w-4 h-4" />
        </Button>
      )
    }
  ];

  const applicationColumns = [
    {
      key: 'applicant',
      header: 'Applicant',
      render: (item: typeof dashboardData.recentApplications[0]) => (
        <div className="flex items-center gap-3">
          <Avatar name={`${item.user?.firstName || ''} ${item.user?.lastName || ''}`} size="sm" />
          <div>
            <p className="font-medium text-secondary-900">{item.user?.firstName} {item.user?.lastName}</p>
            <p className="text-xs text-secondary-500">{item.job?.title}</p>
          </div>
        </div>
      )
    },
    {
      key: 'status',
      header: 'Status',
      width: '120px',
      render: (item: typeof dashboardData.recentApplications[0]) => (
        <Badge variant={getApplicationStatusBadgeVariant(item.status)}>{item.status}</Badge>
      )
    },
    {
      key: 'applied',
      header: 'Applied',
      width: '140px',
      render: (item: typeof dashboardData.recentApplications[0]) => (
        <span className="text-secondary-600">{formatDate(item.createdAt)}</span>
      )
    }
  ];

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Welcome Section */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl p-6 text-white mb-6 shadow-lg">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold">Welcome back, Admin!</h1>
              <p className="text-primary-100 mt-1">Here's what's happening with your platform today.</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/admin/users">
                <Button variant="secondary" size="sm" icon={<UserPlus className="w-4 h-4" />}>
                  Add User
                </Button>
              </Link>
              <Link to="/admin/jobs">
                <Button variant="ghost" size="sm" className="bg-white/10 hover:bg-white/20 text-white">
                  Manage Jobs
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {stats.map((stat, index) => (
            <StatCard
              key={index}
              title={stat.title}
              value={stat.value}
              icon={stat.icon}
              trend={stat.trend}
              color={stat.color}
              subtitle={stat.subtitle}
            />
          ))}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {quickActions.map((action, index) => (
            <Link key={index} to={action.path}>
              <Card hover className="p-4 cursor-pointer group">
                <div className="flex items-center gap-3">
                  <div className={`p-2.5 rounded-lg bg-${action.color}-50 text-${action.color}-600 group-hover:bg-${action.color}-600 group-hover:text-white transition-colors`}>
                    {action.icon}
                  </div>
                  <div>
                    <p className="font-medium text-secondary-900">{action.label}</p>
                    <p className="text-xs text-secondary-500">View & manage</p>
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Users Table */}
          <div className="lg:col-span-2">
            <Card padding="none">
              <CardHeader 
                action={
                  <Link to="/admin/users">
                    <Button variant="ghost" size="sm" icon={<ArrowRight className="w-4 h-4" />} iconPosition="right">
                      View All
                    </Button>
                  </Link>
                }
              >
                <div>
                  <h3 className="text-lg font-semibold text-secondary-900">Recent Users</h3>
                  <p className="text-sm text-secondary-500 mt-0.5">Latest user registrations</p>
                </div>
              </CardHeader>
              <CardBody className="p-0">
                <Table
                  columns={userColumns}
                  data={dashboardData.recentUsers || []}
                  keyExtractor={(item) => item._id}
                  striped={false}
                  emptyMessage="No recent users"
                />
              </CardBody>
            </Card>

            {/* Recent Applications */}
            <Card padding="none" className="mt-6">
              <CardHeader 
                action={
                  <Link to="/admin/jobs">
                    <Button variant="ghost" size="sm" icon={<ArrowRight className="w-4 h-4" />} iconPosition="right">
                      View All
                    </Button>
                  </Link>
                }
              >
                <div>
                  <h3 className="text-lg font-semibold text-secondary-900">Recent Applications</h3>
                  <p className="text-sm text-secondary-500 mt-0.5">Latest job applications</p>
                </div>
              </CardHeader>
              <CardBody className="p-0">
                <Table
                  columns={applicationColumns}
                  data={dashboardData.recentApplications || []}
                  keyExtractor={(item) => item._id}
                  striped={false}
                  emptyMessage="No recent applications"
                />
              </CardBody>
            </Card>
          </div>

          {/* Activity & Quick Links */}
          <div className="space-y-6">
            {/* Activity Stats */}
            <Card>
              <CardHeader>
                <div>
                  <h3 className="text-lg font-semibold text-secondary-900">Platform Activity</h3>
                  <p className="text-sm text-secondary-500 mt-0.5">Last 30 days</p>
                </div>
              </CardHeader>
              <CardBody className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-secondary-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary-100 rounded-lg">
                      <UserPlus className="w-4 h-4 text-primary-600" />
                    </div>
                    <span className="text-sm text-secondary-600">New Users</span>
                  </div>
                  <span className="font-semibold text-secondary-900">+{analyticsData.newUsers}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-secondary-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-success-100 rounded-lg">
                      <Briefcase className="w-4 h-4 text-success-600" />
                    </div>
                    <span className="text-sm text-secondary-600">New Jobs</span>
                  </div>
                  <span className="font-semibold text-secondary-900">+{analyticsData.newJobs}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-secondary-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-warning-100 rounded-lg">
                      <FileText className="w-4 h-4 text-warning-600" />
                    </div>
                    <span className="text-sm text-secondary-600">Applications</span>
                  </div>
                  <span className="font-semibold text-secondary-900">+{analyticsData.newApplications}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-secondary-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-info-100 rounded-lg">
                      <MessageSquare className="w-4 h-4 text-info-600" />
                    </div>
                    <span className="text-sm text-secondary-600">Messages</span>
                  </div>
                  <span className="font-semibold text-secondary-900">{overview.pendingReports}</span>
                </div>
              </CardBody>
            </Card>

            {/* System Status */}
            <Card>
              <CardHeader>
                <div>
                  <h3 className="text-lg font-semibold text-secondary-900">System Status</h3>
                  <p className="text-sm text-secondary-500 mt-0.5">Current health</p>
                </div>
              </CardHeader>
              <CardBody className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-success-500 rounded-full animate-pulse" />
                    <span className="text-sm text-secondary-600">API Server</span>
                  </div>
                  <Badge variant="success" size="sm">Operational</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-success-500 rounded-full animate-pulse" />
                    <span className="text-sm text-secondary-600">Database</span>
                  </div>
                  <Badge variant="success" size="sm">Connected</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-success-500 rounded-full animate-pulse" />
                    <span className="text-sm text-secondary-600">Email Service</span>
                  </div>
                  <Badge variant="success" size="sm">Active</Badge>
                </div>
              </CardBody>
            </Card>

            {/* Revenue Chart Placeholder */}
            <Card>
              <CardHeader>
                <div>
                  <h3 className="text-lg font-semibold text-secondary-900">Revenue</h3>
                  <p className="text-sm text-secondary-500 mt-0.5">Monthly earnings</p>
                </div>
              </CardHeader>
              <CardBody>
                <div className="flex items-end gap-2 h-24">
                  {(dashboardData.monthlyRevenue || []).slice(0, 6).reverse().map((month, index) => {
                    const maxRevenue = Math.max(...(dashboardData.monthlyRevenue || []).map(m => m.revenue), 1);
                    const height = (month.revenue / maxRevenue) * 100;
                    return (
                      <div key={index} className="flex-1 flex flex-col items-center gap-1">
                        <div 
                          className="w-full bg-primary-500 rounded-t transition-all duration-300 hover:bg-primary-600"
                          style={{ height: `${Math.max(height, 4)}%` }}
                        />
                        <span className="text-xs text-secondary-400">{month._id?.slice(5) || ''}</span>
                      </div>
                    );
                  })}
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
